import React, { useEffect, useState } from "react";
import { Card, Button, Badge, Table, Tabs, Tab } from "react-bootstrap";
import api from "../../api";
import ReassignTaskDialog from "./ReassignTaskDialog";
import TaskPerformerManager from "./TaskPerformerManager";
import TaskDelegationManager from "./TaskDelegationManager";
import Can from "../Can";

// Phase 4A, §20/§25. The single combined task-participant view — Official
// Assignee / Assignment History / Performers / Performer Work / Delegations,
// plus the read-only combined timeline (§9). "Assigned To", "Performed By"
// and "Delegated To" are always labeled separately and never merged into
// one generic "assigned" concept, per the task spec's explicit instruction.
function timelineIcon(source) {
  switch (source) {
    case "ASSIGNMENT": return "👤";
    case "PERFORMER": return "🛠️";
    case "WORK_LOG": return "📝";
    case "DELEGATION": return "↪️";
    case "STATUS": return "🔄";
    case "REMARK": return "💬";
    default: return "•";
  }
}

export default function TaskParticipantsPanel({ taskId, assignedTo, assignedToName, readOnly, taskType }) {
  const [showReassign, setShowReassign] = useState(false);
  const [history, setHistory] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeTab, setActiveTab] = useState("performers");

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const [histRes, timelineRes] = await Promise.all([
        api.get(`/tasks/${taskId}/assignment-history`).catch(() => ({ data: { data: [] } })),
        api.get(`/tasks/${taskId}/participant-timeline`).catch(() => ({ data: { data: [] } })),
      ]);
      setHistory(Array.isArray(histRes.data?.data) ? histRes.data.data : []);
      setTimeline(Array.isArray(timelineRes.data?.data) ? timelineRes.data.data : []);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (taskId) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  return (
    <Card className="shadow-sm border-0 tdp-participants-card">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div className="fw-semibold">Task Participants</div>
            <div className="small text-muted">
              <strong>Assigned To:</strong> {assignedToName || "Unassigned"}
            </div>
          </div>
          {!readOnly && (
            <Can moduleKey="task_assignments" actionKey="reassign">
              <Button size="sm" variant="outline-primary" onClick={() => setShowReassign(true)}>Reassign</Button>
            </Can>
          )}
        </div>

        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
          <Tab eventKey="performers" title="Performed By">
            <TaskPerformerManager taskId={taskId} readOnly={readOnly} />
          </Tab>
          <Tab eventKey="delegations" title="Delegated To">
            <TaskDelegationManager taskId={taskId} readOnly={readOnly} />
          </Tab>
          <Tab eventKey="history" title="Assignment History">
            {loadingHistory ? <div className="text-muted small">Loading...</div> : (
              <Table size="sm" responsive>
                <thead><tr><th>When</th><th>Change</th><th>From</th><th>To</th><th>Reason</th><th>By</th></tr></thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td className="small">{new Date(h.changed_at).toLocaleString()}</td>
                      <td><Badge bg="light" text="dark">{h.change_type}</Badge></td>
                      <td className="small">{h.from_assignee_name || "—"}</td>
                      <td className="small">{h.to_assignee_name || "—"}</td>
                      <td className="small">{h.reason || "—"}</td>
                      <td className="small">{h.changed_by_name || "System"}</td>
                    </tr>
                  ))}
                  {!history.length && <tr><td colSpan="6" className="text-muted small">No assignment history recorded yet.</td></tr>}
                </tbody>
              </Table>
            )}
          </Tab>
          <Tab eventKey="timeline" title="Timeline">
            {loadingHistory ? <div className="text-muted small">Loading...</div> : (
              <div className="d-flex flex-column gap-2">
                {timeline.map((e, i) => (
                  <div key={i} className="d-flex gap-2 small">
                    <span>{timelineIcon(e.source)}</span>
                    <span className="text-muted" style={{ minWidth: 150 }}>{new Date(e.at).toLocaleString()}</span>
                    <span>{e.summary}</span>
                  </div>
                ))}
                {!timeline.length && <div className="text-muted small">No participant activity recorded yet.</div>}
              </div>
            )}
          </Tab>
        </Tabs>
      </Card.Body>

      <ReassignTaskDialog
        show={showReassign}
        onHide={() => setShowReassign(false)}
        taskId={taskId}
        currentAssigneeName={assignedToName}
        onReassigned={loadHistory}
        // task_type null = legacy complaint (shown as "IT Support" too).
        task={{ task_type: taskType ?? null, assigned_to: assignedTo, assigned_to_name: assignedToName }}
      />
    </Card>
  );
}
